import { connect } from 'react-redux';
import { addTrackToSavedList, removeTrackFromSavedList } from '../redux/actions/userActions';


import { getAnalytics, logEvent } from "firebase/analytics";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { LABEL_DOMAIN } from "../constants/labels";
import { FOOTER_ACTION_COMPACT_ICON_SIZE } from "../constants/spacing";

import { shareContent, SHARE_FAILURE_MESSAGE } from '../utils/shareContent';
import { fancyTimeFormatWithHM, formatPublishedDate, formatPreviewInfoPublishedDate, isMeaningfullyStarted } from './timeHelper';
import { episodeArtworkURL } from './trackHelper';
import { getYouTubeVideoId } from './youtubeHelper';

import { useEpisodePlayback, finishedProgress } from '../utils/episodePlayback';
import { Menu, MenuItem } from "@mui/material";
import {
    CheckCircleRounded,
    IosShareRounded,
    MoreHorizRounded,
    PlayArrowRounded,
    RadioButtonUncheckedRounded,
    ReportProblemOutlined,
    SmartDisplayOutlined,
} from "@mui/icons-material";

import BookmarkButton from "./BookmarkButton";
import PreviewReportIssueDrawer from './PreviewReportIssueDrawer';
import StatusToast from './StatusToast';

import ProgressBarThin from "./ProgressBarThin";
import SpeakerRows from './SpeakerRows';
import FollowOutline from './FollowOutline';
import useFollowedEntities from './useFollowedEntities';
import useTrackRowLayout from './useTrackRowLayout';
import useAudioPlaybackRate from './useAudioPlaybackRate';
import './TrackInfoRow.css';

const TrackInfoRow = (props) => {
    const {
        trackInfo,
        playTrack,
        hideSource,
        editMode,
        selectionMode,
        selectionDisabled,
        isSelected,
        onSelectionToggle,
        containerStyle,
        containerClassName,
        includesVerticalCardPadding,
        showsBottomDivider,
        relativePublishedDate = false,
        showEpisodeArtwork = false,
        preferredSpeakerId,

        // Redux
        authenticated,
        uid,
        addTrackToSavedList,
        removeTrackFromSavedList,
        savedLists,
        savedTrackIdToSavedListIds,
    } = props;
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const rowLayoutRef = useTrackRowLayout();
    const isFollowed = useFollowedEntities();
    const source = trackInfo && trackInfo.source ? trackInfo.source : {};
    const sourceFollowed = isFollowed('show', { source });
    const sourceId = typeof source.id === 'string' ? source.id.trim() : '';
    const sourceName = typeof source.name === 'string' ? source.name : '';
    const sourceTitle = typeof source.title === 'string' ? source.title : '';
    const sourceImageURL = typeof source.imageURL === 'string' ? source.imageURL.trim() : '';
    const episodeImageURL = episodeArtworkURL(trackInfo);
    const playback = useEpisodePlayback(authenticated ? uid : null, trackInfo);
    const audioPlaybackRate = useAudioPlaybackRate();
    const isVideo = playback.lastPlayedFormat === 'video';
    const selectedProgress = isVideo ? playback.videoProgress : playback.audioProgress;
    // A video can have a different duration from its audio edition. Until its
    // own duration is known, keep the ordinary Play label without inventing one.
    const totalTimeSeconds = Number(isVideo ? selectedProgress?.durationSeconds : selectedProgress?.durationSeconds || trackInfo?.totalTimeSeconds);
    const hasDuration = Number.isFinite(totalTimeSeconds) && totalTimeSeconds > 0;
    const legacyPosition = Number(trackInfo?.lastListenSeconds);
    const audioPosition = Number.isFinite(legacyPosition) && legacyPosition >= 0 ? legacyPosition : 0;
    const displayPosition = selectedProgress?.seconds ?? (isVideo ? 0 : audioPosition);
    const isFinished = finishedProgress(selectedProgress || { seconds: displayPosition, durationSeconds: totalTimeSeconds });
    const playbackStartSeconds = isFinished ? 0 : displayPosition;
    const hasMeaningfulProgress = isMeaningfullyStarted(displayPosition, totalTimeSeconds);
    const progress = hasMeaningfulProgress ? Math.min(1, Math.max(0, displayPosition / totalTimeSeconds)) : 0;
    const remainingSeconds = hasDuration ? Math.max(totalTimeSeconds - playbackStartSeconds, 0) : 0;
    const playbackRate = isVideo ? 1 : audioPlaybackRate;
    const durationLabel = isFinished ? 'Finished' : !hasDuration ? '' : hasMeaningfulProgress ?
        `${fancyTimeFormatWithHM(remainingSeconds / playbackRate)} left` : fancyTimeFormatWithHM(totalTimeSeconds / playbackRate);
    const speedLabel = hasDuration && !isFinished && playbackRate !== 1 ? `@${Number(playbackRate.toFixed(2))}×` : '';
    const publishedDate = trackInfo && trackInfo.publishedDate;
    const publishedDateLabel = relativePublishedDate ?
        formatPreviewInfoPublishedDate(publishedDate) :
        formatPublishedDate(publishedDate);

    const goToPodcast = (podcastId) => () => {
        if (!podcastId) {
            return;
        }
        navigate("/podcast/" + podcastId);
    }

    const openTrackDetails = () => {
        navigate(`/track/${encodeURIComponent(trackInfo.id)}?autoplay=false`);
    };

    const playEpisode = () => {
        if (isVideo) {
            navigate(`/track/${encodeURIComponent(trackInfo.id)}?autoplay=false`, {
                state: { resumeVideo: true, videoToPlay: selectedProgress?.videoId },
            });
        } else {
            playTrack(trackInfo, playbackStartSeconds)();
        }
    };

    const [copiedToastGeneration, setCopiedToastGeneration] = useState(0);
    const [shareError, setShareError] = useState('');
    const [sourceImageFailed, setSourceImageFailed] = useState(false);
    const [failedEpisodeImageURL, setFailedEpisodeImageURL] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [showReportDrawer, setShowReportDrawer] = useState(false);

    const menuOpen = Boolean(menuAnchorEl);
    const displayTitle = sourceTitle.trim() || sourceName.trim() || 'Episode';
    const displayDate = `${publishedDateLabel || 'Episode'}${getYouTubeVideoId(trackInfo?.youtube) ? ' · Video' : ''}`;
    const playbackAction = isFinished ? 'Replay' : displayPosition > 0 ? 'Resume' : 'Play';
    const playbackAccessibilityLabel = `${playbackAction} ${isVideo ? 'video' : 'audio'}: ${displayTitle}${durationLabel ? `, ${durationLabel}` : ''}${speedLabel ? ` ${speedLabel}` : ''}`;
    const showsEpisodeImage = showEpisodeArtwork && episodeImageURL && failedEpisodeImageURL !== episodeImageURL;

    useEffect(() => {
        setSourceImageFailed(false);
    }, [sourceImageURL]);

    const openMenu = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
    };

    const shareTrack = async () => {
        const url = `https://${LABEL_DOMAIN}/track/${encodeURIComponent(trackInfo.id)}`;
        setShareError('');
        setCopiedToastGeneration(0);
        logEvent(analytics, 'share_track');
        try {
            const result = await shareContent({ url });
            if (result === 'copied') setCopiedToastGeneration(generation => generation + 1);
        } catch (_) {
            setShareError(SHARE_FAILURE_MESSAGE);
        }
    }

    const shareFromMenu = () => {
        closeMenu();
        shareTrack();
    };

    const reportFromMenu = () => {
        closeMenu();
        setShowReportDrawer(true);
    };

    const handleSavedListClick = (list) => {
        const scrollContainer = document.getElementById(`editContainer-${trackInfo.id}`);
        scrollContainer.scroll({
            left: 0,
            behavior: 'smooth'
        });
        if (isSavedList(list.id)) {
            removeTrackFromSavedList(trackInfo.id, list.name, list.id);
        } else {
            addTrackToSavedList(trackInfo.id, list.name, list.id);
        }

    }

    const isSavedList = (listId) => {
        return savedTrackIdToSavedListIds[trackInfo.id]?.includes(listId);
    }

    const isTrackSaved = () => {
        return savedTrackIdToSavedListIds[trackInfo.id] !== undefined;
    }

    const saved = isTrackSaved();
    const sourceImageLabel = sourceName.trim().charAt(0).toUpperCase() || 'P';
    const rowClassName = [
        'trackInfoRow',
        'trackInfoRowFlat',
        'trackInfoRowSharedByline',
        selectionMode ? 'trackInfoRowSelectable' : '',
        selectionMode && isSelected ? 'trackInfoRowSelected' : '',
        !includesVerticalCardPadding ? 'trackInfoRowCompact' : '',
        showsBottomDivider ? 'trackInfoRowWithDivider' : '',
        hideSource ? 'trackInfoRowHideSource' : '',
        showsEpisodeImage ? 'trackInfoRowWithEpisodeArtwork' : '',
        containerClassName || '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={rowClassName}
            style={containerStyle || undefined}>
            <StatusToast
                open={copiedToastGeneration > 0}
                instanceKey={copiedToastGeneration}
                message='Copied to clipboard'
                onClose={() => setCopiedToastGeneration(0)}
            />
            <StatusToast tone='error' open={Boolean(shareError)} message={shareError} onClose={() => setShareError('')} />
            <PreviewReportIssueDrawer
                open={showReportDrawer}
                onClose={() => setShowReportDrawer(false)}
                contentType='track'
                contentId={trackInfo.id}
                playerSeconds={0}
            />


            <div className='trackInfoLayout' aria-hidden={selectionMode || undefined}>
                {!hideSource && (
                    <button
                        type='button'
                        onClick={goToPodcast(sourceId)}
                        className='clampSafeButton trackInfoSourceButton'
                        aria-label={`${sourceName.trim() ? `Open ${sourceName} show` : 'Open show'}${sourceFollowed ? ', followed' : ''}`}
                        disabled={!sourceId || selectionMode}
                    >
                        <FollowOutline followed={sourceFollowed}>
                        {sourceImageURL && !sourceImageFailed ? (
                            <img
                                src={sourceImageURL}
                                alt={sourceName || 'Show'}
                                className='trackInfoSourceImage'
                                onError={() => setSourceImageFailed(true)}
                            />
                        ) : (
                            <div className='trackInfoSourceImage trackInfoSourceImageFallback' aria-hidden='true'>
                                {sourceImageLabel}
                            </div>
                        )}
                        </FollowOutline>
                    </button>
                )}

                <div className='trackInfoContent'>
                    <div className='trackInfoRowContentLayout' ref={rowLayoutRef}>
                        <div className='trackInfoByline'>
                            <div className='trackInfoHeader'>
                                <button
                                    type='button'
                                    onClick={openTrackDetails}
                                    className='clampSafeButton trackInfoTitleButton'
                                    disabled={selectionMode}
                                >
                                    <div className='trackInfoSourceTitle'>
                                        {displayTitle}
                                    </div>
                                </button>

                                <div className='trackInfoPublishedDate'>
                                    {displayDate}
                                </div>

                            </div>

                            <SpeakerRows speakers={trackInfo.speakers} collapseMultiple interactive={!selectionMode}
                                prioritizeFollowed preferredSpeakerId={preferredSpeakerId} />

                            {hasMeaningfulProgress && (
                                <button
                                    type='button'
                                    className='trackInfoProgressButton'
                                    onClick={playEpisode}
                                    disabled={selectionMode}
                                    aria-label={playbackAccessibilityLabel}
                                >
                                    <ProgressBarThin progress={progress} />
                                </button>
                            )}
                        </div>

                        {showsEpisodeImage && (
                            <button
                                type='button'
                                className='trackInfoEpisodeImageButton'
                                onClick={openTrackDetails}
                                aria-label={`Open episode: ${displayTitle}`}
                                disabled={selectionMode}
                            >
                                <img
                                    src={episodeImageURL}
                                    alt=''
                                    className='trackInfoEpisodeImage podcastArtwork'
                                    loading='lazy'
                                    decoding='async'
                                    onError={() => setFailedEpisodeImageURL(episodeImageURL)}
                                />
                            </button>
                        )}

                        <div className='trackInfoFooter'>
                            <button
                                type='button'
                                onClick={playEpisode}
                                className='trackInfoPlayPill blurBackground'
                                disabled={selectionMode}
                                aria-label={playbackAccessibilityLabel}
                            >
                                {isVideo ? (
                                    <SmartDisplayOutlined className='trackInfoPlayPillIcon' />
                                ) : (
                                    <PlayArrowRounded className='trackInfoPlayPillIcon' />
                                )}
                                <span className={durationLabel === 'Finished' ? 'trackInfoPlayPillLabel trackInfoPlayPillLabelStrong' : 'trackInfoPlayPillLabel'}>
                                    {durationLabel || 'Play'}
                                    {speedLabel && <> <span className='trackInfoPlayPillSpeed'>{speedLabel}</span></>}
                                </span>
                            </button>

                            <span className='trackInfoFooterSpacer' />

                            {selectionMode ? (
                                <span
                                    className={isSelected ? 'trackInfoSelectionIndicator trackInfoSelectionIndicatorSelected' : 'trackInfoSelectionIndicator'}
                                    aria-hidden='true'
                                >
                                    {isSelected ? (
                                        <CheckCircleRounded style={{ fontSize: '34px' }} />
                                    ) : (
                                        <RadioButtonUncheckedRounded style={{ fontSize: '34px' }} />
                                    )}
                                </span>
                            ) : (
                                <div className='trackInfoFooterActions'>
                                    <BookmarkButton
                                        trackId={trackInfo.id}
                                        editMode={editMode}
                                        source={trackInfo.source}
                                        buttonClassName='trackInfoSaveButton'
                                        iconStyle={{
                                            verticalAlign: 'middle',
                                            fontSize: FOOTER_ACTION_COMPACT_ICON_SIZE,
                                            width: FOOTER_ACTION_COMPACT_ICON_SIZE,
                                            height: FOOTER_ACTION_COMPACT_ICON_SIZE,
                                        }} />

                                    <button
                                        type='button'
                                        className='trackInfoMoreButton'
                                        aria-label='More options'
                                        aria-controls={menuOpen ? `track-info-menu-${trackInfo.id}` : undefined}
                                        aria-haspopup='true'
                                        aria-expanded={menuOpen ? 'true' : undefined}
                                        onClick={openMenu}
                                    >
                                        <MoreHorizRounded className='trackInfoMoreButtonIcon' />
                                    </button>
                                    <Menu
                                        id={`track-info-menu-${trackInfo.id}`}
                                        anchorEl={menuAnchorEl}
                                        open={menuOpen}
                                        onClose={closeMenu}
                                    >
                                        <MenuItem onClick={shareFromMenu}>
                                            <IosShareRounded className='trackInfoMenuIcon' />
                                            <span className='menuOption'>Share</span>
                                        </MenuItem>
                                        {authenticated && (
                                            <MenuItem onClick={reportFromMenu}>
                                                <ReportProblemOutlined className='trackInfoMenuIcon' />
                                                <span className='menuOption'>Report issue</span>
                                            </MenuItem>
                                        )}
                                    </Menu>
                                </div>
                            )}
                        </div>
                    </div>

                    {editMode && !selectionMode && saved && savedLists && (savedLists.length > 0) && (
                        <div id={`editContainer-${trackInfo.id}`} className='horizontalScroll hideScrollbar' style={{ marginTop: '16px' }}>
                            {savedLists.map((savedList) => (
                                isSavedList(savedList.id) ?
                                    <button key={savedList.id} onClick={() => handleSavedListClick(savedList)}>
                                        <div className='savedList selectedSavedList'>
                                            {savedList.name}
                                        </div>
                                    </button> : null
                            ))}

                            {savedLists.map((savedList) => (
                                !isSavedList(savedList.id) ?
                                    <button key={savedList.id} onClick={() => handleSavedListClick(savedList)}>
                                        <div className='savedList savedListTrackRowInfo'>
                                            {savedList.name}
                                        </div>
                                    </button> : null
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selectionMode && onSelectionToggle && (
                <button
                    type='button'
                    className='trackInfoSelectionOverlay'
                    disabled={selectionDisabled}
                    onClick={onSelectionToggle}
                    aria-label={`${isSelected ? 'Deselect' : 'Select'} ${sourceTitle || 'episode'}`}
                />
            )}
        </div>
    );
}


TrackInfoRow.propTypes = {

}

TrackInfoRow.defaultProps = {
    containerStyle: null,
    containerClassName: '',
    selectionMode: false,
    isSelected: false,
    onSelectionToggle: null,
    includesVerticalCardPadding: true,
    showsBottomDivider: false,

}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
    savedLists: state.user.savedLists,
    savedTrackIdToSavedListIds: state.user.savedTrackIdToSavedListIds,
})

const mapActionsToProps = {
    addTrackToSavedList,
    removeTrackFromSavedList,
}

export default connect(mapStateToProps, mapActionsToProps)(TrackInfoRow);
